import './Subscriptions.sass';

import React, { FC, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Header, List, ListItem, Placeholder, ScrollView } from '$uikit';
import { subscriptionsActions, subscriptionsSelector } from '$store/subscriptions';
import { Loader, SubscriptionIcon } from '$shared/components';
import { useTranslator } from '$hooks';
import { CurrencyEnum, SubscriptionType } from '$store/models';
import { mainSelector } from '$store/main';
import { SUBSCRIPTION_INFO } from '$shared/constants/pages';
import { subscriptionPayRoute } from '$utils/subscriptionPayRoute';
import { ContentPriorityType } from '$store/main/interface';

export const Subscriptions: FC = () => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const { isLoading, isFailed, subscriptions } = useSelector(subscriptionsSelector);
  const { users, contentPriority } = useSelector(mainSelector);

  useEffect(() => {
    dispatch(subscriptionsActions.loadSubscriptions());
  }, []);

  const handleRetry = useCallback(() => {
    dispatch(subscriptionsActions.loadSubscriptions());
  }, []);

  function renderContent() {
    if (isLoading && !subscriptions.length) {
      return <Loader isWrapped />;
    }

    if (isFailed) {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={handleRetry}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    return (
      <div className="Subscriptions__content">
        <List>
          {subscriptions.map((item) => {
            if (
              contentPriority === ContentPriorityType.ADULT &&
              item.type === SubscriptionType.Media &&
              item.isActive === false
            )
              return;

            let title: string;
            if (item.type === SubscriptionType.Adult) {
              title = t('subscriptions_type_adult');
            } else if (item.type === SubscriptionType.Media) {
              title = t('subscriptions_type_media');
            } else if (item.type === SubscriptionType.Basic) {
              title = 'Ton.Place Basic';
            } else {
              const user = users[item.authorId];
              title = user?.firstName + ' ' + user?.lastName;
            }

            let description = t('subscriptions_subscribe');
            if (item.isActive) {
              let price: string;
              if (
                item.currency === CurrencyEnum.TON ||
                item.currency === CurrencyEnum.PLACE
              ) {
                price = `${item.price} ${item.currency.toUpperCase()}`;
              } else {
                price = `€${item.price}`;
              }

              description =
                price +
                ' / ' +
                t('subscriptions_price_period', {
                  count: item.period,
                });

              if (!item.price) {
                description = t('subscription_price_free');
              }
            }
            if (item.isSuspended) {
              description = t('subscriptions_suspended', {
                defaultValue: 'Paused — payment failed',
              });
            }

            const { name: routeName, params: routeParams } =
              item.isActive || item.isSuspended
                ? { name: SUBSCRIPTION_INFO, params: { id: item.id } }
                : subscriptionPayRoute(item.type, item.authorId);

            return (
              <ListItem
                key={item.id}
                chevron
                description={description}
                before={<SubscriptionIcon type={item.type} authorId={item.authorId} />}
                routeName={routeName}
                routeParams={routeParams}
              >
                {title}
              </ListItem>
            );
          })}
        </List>
      </div>
    );
  }

  return (
    <>
      <Header showBackButton>{t('subscriptions_title')}</Header>
      <ScrollView>{renderContent()}</ScrollView>
    </>
  );
};
